/* eslint-disable unused-imports/no-unused-vars */
import React from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Slider,
} from '@mui/material';
import Typography from '@/components/typography/typography';
import TextField from '@/components/text-field/text-field';
import Select from '@/components/select/select';
import ColorPickerInput from '@/components/color-picker-input/color-picker-input';
import BorderControl from './border-control';

interface ButtonControlsProps {
  attrs: Record<string, any>;
  updateAttrs: (attr: Record<string, any>) => void;
}



const ButtonControls: React.FC<ButtonControlsProps> = ({
  attrs,
  updateAttrs
}) => {

  const { text, styles } = attrs;

  return (
    <>
      <TextField
        label="Button Text"
        fullWidth
        value={text}
        onChange={(e) => updateAttrs({ text: e.target.value })}
        size="small"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Link URL"
        placeholder="https://..."
        fullWidth
        value={attrs.href || ''}
        onChange={(e) => updateAttrs({ href: e.target.value })}
        size="small"
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2 }}>
        <Typography type="s3" color='theme.neutral.800'>
          Font Size
        </Typography>
        <Slider
          value={styles?.fontSize || 16}
          onChange={(_, value) => updateAttrs({ styles: { fontSize: value as number } })}
          min={10}
          max={40}
          step={1}
          valueLabelDisplay="auto"
          size="small"
          sx={(theme) => ({
            '& .MuiSlider-rail': {
              color: theme.palette.theme.primary[500],
            },
            '& .MuiSlider-thumb': {
              color: theme.palette.theme.primary[800],
            },
            '& .MuiSlider-track': {
              color: theme.palette.theme.primary[600],
            },
          })}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <ColorPickerInput
          value={styles?.color || '#333333'}
          onChange={(color) => updateAttrs({ styles: { color: color } })}
          label='Text Color'
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <ColorPickerInput
          value={styles?.backgroundColor || '#333333'}
          onChange={(color) => updateAttrs({ styles: { backgroundColor: color } })}
          label='Background Color'
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography type="s3" color='theme.neutral.800'>
            Width (px)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Slider
            value={Math.min(Math.max(Math.round(Number(styles?.width) || 130), 20), 794)}
            onChange={(_, value) => updateAttrs({ styles: { width: value as number } })}
            min={20}
            max={794}
            step={1}
            valueLabelDisplay="auto"
            size="small"
            sx={(theme) => ({
              flex: 1,
              '& .MuiSlider-rail': {
                color: theme.palette.theme.primary[500],
              },
              '& .MuiSlider-thumb': {
                color: theme.palette.theme.primary[800],
              },
              '& .MuiSlider-track': {
                color: theme.palette.theme.primary[600],
              },
            })}
          />
          <TextField
            type="number"
            size="small"
            value={Math.round(Number(styles?.width) || 130)}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val) && val > 0) {
                updateAttrs({ styles: { width: val } });
              }
            }}
            inputProps={{ min: 20, max: 794, style: { padding: '4px 6px', fontSize: '13px', textAlign: 'right' } }}
            sx={{ width: '75px', flexShrink: 0 }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography type="s3" color='theme.neutral.800'>
            Height (px)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Slider
            value={Math.min(Math.max(Math.round(Number(styles?.height) || 40), 20), 500)}
            onChange={(_, value) => updateAttrs({ styles: { height: value as number } })}
            min={20}
            max={500}
            step={1}
            valueLabelDisplay="auto"
            size="small"
            sx={(theme) => ({
              flex: 1,
              '& .MuiSlider-rail': {
                color: theme.palette.theme.primary[500],
              },
              '& .MuiSlider-thumb': {
                color: theme.palette.theme.primary[800],
              },
              '& .MuiSlider-track': {
                color: theme.palette.theme.primary[600],
              },
            })}
          />
          <TextField
            type="number"
            size="small"
            value={Math.round(Number(styles?.height) || 40)}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (!isNaN(val) && val > 0) {
                updateAttrs({ styles: { height: val } });
              }
            }}
            inputProps={{ min: 20, max: 500, style: { padding: '4px 6px', fontSize: '13px', textAlign: 'right' } }}
            sx={{ width: '75px', flexShrink: 0 }}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 0 }}>
        <BorderControl
          styles={styles || {}}
          onChange={(key, value) => updateAttrs({ styles: { ...styles, [key]: value } })}
          isBordered={styles?.is_bordered || false}
          disableCollapse={true}
          initiateStyles={(newStyles) => updateAttrs({ styles: { ...styles, ...newStyles } })}
        />
      </Box>
    </>
  );
};

export default ButtonControls;